import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Box, Button, Typography, Paper } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import sh from "bundles/common/utils/sh";
import HTML from "bundles/common/components/HTML";
import GalleryCarousel from "./GalleryCarousel";

const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      fontSize: "1.25rem",
      color: "hsl(222.2, 84%, 4.9%)",
      letterSpacing: "-0.01em",
      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
      display: "inline-block",
      borderBottom: "2px solid hsl(152, 60%, 32%)",
      pb: "0.25rem",
      mb: 2.5,
    }}
  >
    {children}
  </Typography>
);

const StatCard = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: "0.75rem",
      p: "1rem",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
        color: "hsl(215.4, 16.3%, 46.9%)",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        mb: "0.375rem",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "1.125rem",
        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
        color: "hsl(153, 68%, 25%)",
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const ProjectPage = () => {
  const { state } = useLocation();

  const authenticityToken = useSelector((state) => state.authenticity_token);
  const params = useParams();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentRecruiter = useSelector((state) => state.current_recruiter);

  const [project, setProject] = useState(state?.project);
  const [projectCompany, setProjectCompany] = useState();

  const fetchProject = () => {
    sh.get(`/projects/${params.id}`, {
      authenticity_token: authenticityToken,
    })
      .then((res) => {
        if (res?.data) {
          setProject(res.data?.project);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchCompany = (companyId) => {
    sh.get(`/company/${companyId}`, {
      authenticity_token: authenticityToken,
    })
      .then((res) => {
        if (res?.data) {
          setProjectCompany(res.data);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (!project?.id) {
      fetchProject();
    }
  }, []);

  useEffect(() => {
    if (project?.company_id) {
      fetchCompany(project.company_id);
    }
  }, [project]);

  const companyHref = currentRecruiter
    ? `/${i18nLocale}/recruiter/company`
    : `/${i18nLocale}/companies/${projectCompany?.encoded_name}/${project?.company_id}`;

  const hasGallery = project?.gallery_pictures_records?.length > 0;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "hsl(0, 0%, 100%)" }}>
      <Box
        sx={{
          mx: "auto",
          maxWidth: "72rem",
          px: { xs: "1rem", md: "2rem" },
          py: { xs: "1.5rem", md: "2.5rem" },
        }}
      >
        {/* Cover */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: "12rem", md: "22rem" },
            borderRadius: "0.75rem",
            overflow: "hidden",
            bgcolor: "hsl(210, 40%, 96.1%)",
          }}
        >
          <Box
            component="img"
            src={project?.cover_picture_url}
            srcSet={`${project?.cover_picture_url} 1x, ${project?.small_cover_picture_url} 0.5x`}
            alt={project?.title || "Project cover"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />
          <Button
            href={companyHref}
            disableRipple
            startIcon={<ArrowLeft size={16} />}
            sx={{
              position: "absolute",
              top: "1rem",
              left: "1rem",
              px: "1rem",
              height: "auto",
              minHeight: 0,
              py: "0.4rem",
              fontSize: "0.875rem",
              borderRadius: "9999px",
              backgroundColor: "hsla(0, 0%, 100%, 0.9)",
              backdropFilter: "blur(4px)",
              color: "hsl(222.2, 84%, 4.9%)",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              transition: "background-color 0.15s",
              "&:hover": {
                backgroundColor: "hsl(0, 0%, 100%)",
              },
            }}
          >
            <FormattedMessage id="settings.company" />
          </Button>
        </Box>

        {/* Identity */}
        <Box sx={{ mt: "1.5rem" }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "1.5rem", md: "1.875rem" },
              color: "hsl(222.2, 84%, 4.9%)",
              letterSpacing: "-0.025em",
              lineHeight: 1.2,
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            {project?.title}
          </Typography>
          {projectCompany?.name && (
            <Typography
              sx={{
                mt: "0.375rem",
                fontSize: "1rem",
                color: "hsl(215.4, 16.3%, 46.9%)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              {projectCompany.name}
            </Typography>
          )}
          {project?.location && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                mt: "0.5rem",
                color: "hsl(215.4, 16.3%, 46.9%)",
              }}
            >
              <MapPin size={16} />
              <Typography sx={{ fontSize: "0.875rem", color: "inherit" }}>
                {project.location}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Stats */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: "1rem",
            mt: "1.75rem",
          }}
        >
          <StatCard
            label={<FormattedMessage id="project.end_date_year" />}
            value={
              project?.end_date ? (
                new Date(project.end_date).getFullYear()
              ) : (
                <FormattedMessage id="undefined" />
              )
            }
          />
          <StatCard
            label={<FormattedMessage id="project.status_only" />}
            value={<FormattedMessage id={`project.status_values.${project?.status}`} />}
          />
          <StatCard
            label={<FormattedMessage id="project.client_only" />}
            value={project?.client_name || <FormattedMessage id="undefined" />}
          />
        </Box>

        {/* Description */}
        <Box sx={{ mt: "2.5rem" }}>
          <SectionHeader>
            <FormattedMessage id="project.only_description" />
          </SectionHeader>
          <Box
            sx={{
              color: "hsl(215.4, 16.3%, 46.9%)",
              fontSize: "0.9375rem",
              lineHeight: 1.85,
            }}
          >
            <HTML html={project?.description} />
          </Box>
        </Box>

        {/* Gallery */}
        <Box sx={{ mt: "2.5rem" }}>
          <SectionHeader>
            <FormattedMessage id="project.gallery_only" />
          </SectionHeader>
          {hasGallery ? (
            <GalleryCarousel project={project} />
          ) : (
            <Typography
              sx={{ fontSize: "0.875rem", color: "hsl(215.4, 16.3%, 46.9%)" }}
            >
              <FormattedMessage id="project.no_picture" />
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: "3rem" }}>
          <Button
            href={companyHref}
            disableRipple
            sx={{
              px: "1.5rem",
              py: "0.5rem",
              minHeight: 0,
              borderRadius: "9999px",
              fontSize: "0.875rem",
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, sans-serif",
              fontWeight: 400,
              backgroundColor: "hsl(153 68% 25%)",
              color: "white",
              transition: "all 180ms ease",
              "&:hover": {
                backgroundColor: "hsl(153 68% 25% / 0.9)",
              },
            }}
          >
            <FormattedMessage id="project.more_projects" />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectPage;
